'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from '@/lib/axios';
import { ApiRoutes } from '@/settings/routes';
import LoadingFront from '@/components/front/ui/LoadingFront';
import PaginationFront from '@/components/front/ui/PaginationFront';
import ArticlesTerm from '@/components/front/ui/ArticlesTerm';

const normalizeArticles = (incoming) => {
    if (!incoming) return { items: [], pagination: null, links: null };
    if (Array.isArray(incoming)) return { items: incoming, pagination: null, links: null };
    if (Array.isArray(incoming.data)) return { items: incoming.data, pagination: incoming, links: incoming.links || null };
    return { items: [], pagination: null, links: null };
};

export default function PaginatedCategoryArticles({ initialArticles, categorySlug }) {
    const initial = useMemo(() => normalizeArticles(initialArticles), [initialArticles]);
    const [loading, setLoading] = useState(false);
    const [articles, setArticles] = useState(initial.items);
    const [pagination, setPagination] = useState(initial.pagination);
    const [paginationLinks, setPaginationLinks] = useState(initial.links);

    const applyResponse = useCallback((res) => {
        const normalized = normalizeArticles(res?.data?.articles);
        setArticles(normalized.items);
        setPagination(normalized.pagination);
        setPaginationLinks(normalized.links);
    }, []);

    const fetchData = useCallback(
        async (url = `${ApiRoutes.API_get_category_front}${categorySlug}`) => {
            if (!categorySlug) return;
            setLoading(true);
            try {
                const res = await axios.get(url);
                if (res?.data?.status === 200) applyResponse(res);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        },
        [applyResponse, categorySlug],
    );

    const nextData = useCallback((url) => fetchData(`${url.replace(ApiRoutes.API_ROUT, '')}`), [fetchData]);
    const prevData = useCallback((url) => fetchData(`${url.replace(ApiRoutes.API_ROUT, '')}`), [fetchData]);
    const pageData = useCallback((page) => fetchData(`${ApiRoutes.API_get_category_front}${categorySlug}?page=${page}`), [fetchData, categorySlug]);

    useEffect(() => {
        const next = normalizeArticles(initialArticles);
        setArticles(next.items);
        setPagination(next.pagination);
        setPaginationLinks(next.links);
    }, [initialArticles]);

    if (loading) return <LoadingFront />;

    if (!articles || articles.length === 0) {
        return <div className="p-8 text-center text-gray-600 rounded bg-custom-orange-100">No articles available.</div>;
    }

    return (
        <div className="w-full">
            <div className="grid grid-cols-1 gap-10 md:grid-cols-2 xl:grid-cols-3">
                {articles.map((article) => (
                    <ArticlesTerm key={article.id} articleData={article} />
                ))}
            </div>

            {pagination && paginationLinks && (
                <nav className="mt-10">
                    <PaginationFront pagination={pagination} paginationLinks={paginationLinks} prevPage={prevData} nextPage={nextData} page={pageData} />
                </nav>
            )}
        </div>
    );
}

